import React, { useCallback, useRef, useState, useEffect } from "react";
import { Formik, Form, FieldArray, useFormikContext } from "formik";
import * as Yup from "yup";
import Button from "apollo-react/components/Button";
import MenuItem from "apollo-react/components/MenuItem";
import Grid from "apollo-react/components/Grid";
import Typography from "apollo-react/components/Typography";
import Paper from "apollo-react/components/Paper";
import Box from "apollo-react/components/Box";
import IconButton from "apollo-react/components/IconButton";
import Autocomplete from "apollo-react/components/Autocomplete";
import PhoneNumberInput from "apollo-react/components/PhoneNumberInput";
import Plus from "apollo-react-icons/Plus";
import Trash from "apollo-react-icons/Trash";
import StatusCheck from "apollo-react-icons/StatusCheck";
import {
  FormikTextField,
  FormikSelect,
  FormikDocumentUpload,
} from "../../../../common/FormikComponent";
import { useMultiForm } from "../../../../../contexts/MultiFormContext";
import axiosInstance from '../../../../../axiosInstance';
import CommentsList from '../../../../common/FormApproveComponents/CommentsList/CommentsList';
import CommentToggle from '../../../../common/CommentToggle/CommentToggle';
import { COUNTRIES } from '../../../../../constants/countries';

// component to sync Formik values to context when they change
const FormikContextSync = ({ onValuesChange }) => {
  const { values } = useFormikContext();
  const prevValuesRef = useRef(null);
  
  useEffect(() => {
    // only sync if values actually changed
    const valuesStr = JSON.stringify(values);
    if (prevValuesRef.current !== valuesStr) {
      prevValuesRef.current = valuesStr;
      onValuesChange(values);
    }
  }, [values, onValuesChange]);
  
  return null;
};

const validationSchema = Yup.object({
  drug_name_brand: Yup.string().required("Drug name is required"),
  generic_name: Yup.string().nullable(),
  dosage_form: Yup.string().nullable(),
  strength: Yup.string().nullable(),
  manufacturer_name: Yup.string().nullable(),
  country_of_origin: Yup.object().nullable(),
  marketing_authorization_holder: Yup.string().nullable(),
  proposed_price_local: Yup.number()
    .nullable()
    .positive("Price must be positive"),
  contact_email: Yup.string()
    .nullable()
    .email("Invalid email format"),
  phone_number: Yup.string()
    .nullable()
    .matches(
      /^[0-9+\-() ]+$/,
      "Phone number can only contain numbers and special characters (+, -, (, ), space)"
    ),
  reference_country_prices: Yup.array().of(
    Yup.object().shape({
      country: Yup.string()
        .nullable()
        .test(
          'required-with-others',
          'Country is required',
          function(value) {
            const { price, currency } = this.parent || {};
            const hasPrice = price !== null && price !== undefined && price !== '';
            const hasCurrency = currency && currency.trim() !== '';
            
            if (!hasPrice && !hasCurrency) {
              return true;
            }
            
            return value && value.trim() !== '';
          }
        ),
      price: Yup.number()
        .nullable()
        .positive("Price must be positive")
        .test(
          'required-with-others',
          'Price is required',
          function(value) {
            const { country, currency } = this.parent || {};
            const hasCountry = country && country.trim() !== '';
            const hasCurrency = currency && currency.trim() !== '';
            
            if (!hasCountry && !hasCurrency) {
              return true;
            }
            
            return value !== null && value !== undefined && value !== '';
          }
        ),
      currency: Yup.string()
        .nullable()
        .test(
          'required-with-others',
          'Currency is required',
          function(value) {
            const { country, price } = this.parent || {};
            const hasCountry = country && country.trim() !== '';
            const hasPrice = price !== null && price !== undefined && price !== '';
            
            if (!hasCountry && !hasPrice) {
              return true;
            }
            
            return value && value.trim() !== '';
          }
        ),
    })
  ),
});

// Helper component to mark all fields as touched on mount
const TouchAllFieldsOnMount = ({ setTouched, values }) => {
  useEffect(() => {
    const touchedFields = {
      drug_name_brand: true,
      generic_name: true,
      dosage_form: true,
      strength: true,
      manufacturer_name: true,
      country_of_origin: true,
      marketing_authorization_holder: true,
      local_agent_distributor: true,
      proposed_price_local: true,
      currency: true,
      cif_price: true,
      ex_factory_price: true,
      packaging_details: true,
      contact_email: true,
      phone_number: true,
      remarks_justification: true,
      reference_country_prices: values.reference_country_prices.map(() => ({
        country: true,
        price: true,
        currency: true
      }))
    };
    setTouched(touchedFields);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  
  return null;
};

const DrugPricingForm = ({ formId = null, onSuccess = null, isSendbackMode = false }) => {
  
  const formRef = useRef(null);
  const { 
    drugPricing,
    setFormDataWithValidation,
    errors,
    submitAllForms,
    validity,
  } = useMultiForm();

  const [comments, setComments] = useState([]);

  const fetchComments = useCallback(async () => {
    if (!drugPricing?.id) return;
    const response = await axiosInstance.get(`/api/drug-pricing-form-comments/enduser/${drugPricing?.id}`);
    setComments(response.data || []);
  }, [drugPricing]);

  useEffect(() => {
    if (drugPricing?.id) {
      fetchComments();
    }
  }, [drugPricing, fetchComments]);

  const handleAddComment = async (comment, subFormId, fileId = null, fileData = null) => {
    try {   
        const requestData = {
            drugPricingFormId: drugPricing?.id,
            comment,
        };
        
        // Add file ID if provided
        if (fileId) {
            requestData.additional_file_id = fileId;
        }

        const response = await axiosInstance.post(`/api/drug-pricing-form-comments/enduser`, requestData);

        // Enrich the response with file data if available
        // set isOwner to true since the current user just created this comment
        const enrichedComment = { ...response.data, isOwner: true };
        if (fileData && fileId) {
            // Convert fileData to the expected additional_file format
            enrichedComment.additional_file = {
                id: fileData.id,
                original_name: fileData.original_name,
                file_path: null, // Not provided by upload response
                file_size: fileData.file_size,
                mime_type: fileData.mime_type,
                createdAt: fileData.created_at,
                updatedAt: fileData.created_at,
                deletedAt: null,
                user_id: null, // Will be set by backend
                end_user_id: null, // Will be set by backend
                ...fileData // Include any additional fields
            };
        }

        console.log("Comment added with file data:", enrichedComment);
        setComments(prev => [...prev, enrichedComment]);
    } catch (error) {
        console.error("Error adding comment:", error);
        throw error;
    }
  };

  // handle edit comment
  const handleEditComment = async (commentId, newText, attachmentInfo = {}) => {
    try {
      const requestData = { comment: newText };

      if (attachmentInfo.remove_attachment) {
        requestData.remove_attachment = true;
      } else if (attachmentInfo.new_file_id) {
        requestData.additional_file_id = attachmentInfo.new_file_id;
      }

      await axiosInstance.put(`/api/drug-pricing-form-comments/enduser/${commentId}`, requestData);
      fetchComments();
    } catch (error) {
      console.error('error editing comment:', error);
      throw error;
    }
  };

  // handle delete comment
  const handleDeleteComment = async (commentId) => {
    try {
      await axiosInstance.delete(`/api/drug-pricing-form-comments/enduser/${commentId}`);
      setComments(prev => prev.filter(c => c.id !== commentId));
    } catch (error) {
      console.error('error deleting comment:', error);
      throw error;
    }
  };

  // Track the last data sent to avoid validation loops
  const lastValidationDataRef = useRef(null);

  // Trigger validation when form is initialized with existing data
  useEffect(() => {
    if (drugPricing && Object.keys(drugPricing).length > 0) {
      // Check if form has meaningful data (not just default/empty values)
      const hasData = Object.entries(drugPricing).some(([key, value]) => {
        if (key === 'reference_country_prices' && Array.isArray(value)) {
          return value.some(item => item.country || item.price || item.currency);
        }
        return value && value !== '';
      });
      
      if (hasData) {
        // Only trigger validation if the data has actually changed
        const currentDataString = JSON.stringify(drugPricing);
        if (lastValidationDataRef.current !== currentDataString) {
          lastValidationDataRef.current = currentDataString;
          setFormDataWithValidation('drugPricing', drugPricing);
        }
      }
    }
  }, [drugPricing, setFormDataWithValidation]);

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      const transformedValues = {
        ...values,
        country_of_origin: values.country_of_origin?.label || values.country_of_origin || ''
      };
      setFormDataWithValidation('drugPricing', transformedValues);
      const validation = await submitAllForms();
      
      if (validation && onSuccess) {
        onSuccess(validation);
      }
    } catch (error) {

    } finally {
      setSubmitting(false);
    }
  };

  // ref to track pending sync timeout
  const syncTimeoutRef = useRef(null);
  const lastSyncedValuesRef = useRef(null);

  // sync handler for FormikContextSync - handles all value changes
  const handleValuesSync = useCallback((values) => {
    // clear any pending sync
    if (syncTimeoutRef.current) {
      clearTimeout(syncTimeoutRef.current);
      syncTimeoutRef.current = null;
    }
    
    const transformedValues = {
      ...values,
      country_of_origin: values.country_of_origin?.label || values.country_of_origin || ''
    };
    
    const currentValuesString = JSON.stringify(transformedValues);
    
    // skip if values haven't changed
    if (lastSyncedValuesRef.current === currentValuesString) {
      return;
    }
    
    // debounce regular input changes
    syncTimeoutRef.current = setTimeout(() => {
      if (lastSyncedValuesRef.current !== currentValuesString) {
        lastSyncedValuesRef.current = currentValuesString;
        setFormDataWithValidation('drugPricing', transformedValues);
      }
    }, 150);
  }, [setFormDataWithValidation]);

  // cleanup on unmount
  useEffect(() => {
    return () => {
      if (syncTimeoutRef.current) {
        clearTimeout(syncTimeoutRef.current);
      }
    };
  }, []);

  return (
    <>
    <Paper elevation={1} sx={{ padding: 4, margin: 0 }}>
      <Formik
        initialValues={{
          ...drugPricing,
          country_of_origin: typeof drugPricing.country_of_origin === 'string' && drugPricing.country_of_origin
            ? COUNTRIES.find(c => c.label === drugPricing.country_of_origin) || null
            : drugPricing.country_of_origin || null,
          reference_country_prices: drugPricing.reference_country_prices?.length > 0 
            ? drugPricing.reference_country_prices 
            : [{ country: '', price: '', currency: '' }]
        }}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize={true}
        validateOnMount={true}
        validateOnChange={true}
        validateOnBlur={true}
      >
        {({ values, isSubmitting, errors: formikErrors, isValid, dirty, setFieldValue, touched, setTouched, validateForm }) => {
          const contextErrors = errors.drugPricing;
          const hasContextErrors = contextErrors?.hasErrors;
          const generalError = contextErrors?.general;
          
          const combinedFieldErrors = { ...formikErrors };
          if (contextErrors?.fields) {
            Object.entries(contextErrors.fields).forEach(([fieldName, fieldError]) => {
              if (!combinedFieldErrors[fieldName] && fieldError?.error) {
                combinedFieldErrors[fieldName] = fieldError.error;
              }
            });
          }
          
          const displayErrors = hasContextErrors && generalError ? { general: generalError } : combinedFieldErrors;
          
          return (
            <>
            {/* sync Formik values to context */}
            <FormikContextSync onValuesChange={handleValuesSync} />
          <Form ref={formRef}>
            <TouchAllFieldsOnMount setTouched={setTouched} values={values} />
            <Box sx={{ marginBottom: 4 }}>
              <Typography
                variant="title1"
                component="h2"
                sx={{
                  fontWeight: 500,
                  marginBottom: 3,
                  color: "#333",
                  paddingBottom: 1,
                  borderBottom: "1px solid #e0e0e0",
                }}
              >
                Drug Information
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <FormikTextField
                    name="drug_name_brand"
                    label="Drug Name (Brand)"
                    placeholder="Enter brand name"
                    required
                    fullWidth
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormikTextField
                    name="generic_name"
                    label="Generic Name"
                    placeholder="Enter generic name"
                    fullWidth
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormikSelect
                    name="dosage_form"
                    label="Dosage Form"
                    fullWidth
                  >
                    <MenuItem value="">Select dosage form</MenuItem>
                    <MenuItem value="tablet">Tablet</MenuItem>
                    <MenuItem value="capsule">Capsule</MenuItem>
                    <MenuItem value="injection">Injection</MenuItem>
                    <MenuItem value="syrup">Syrup</MenuItem>
                    <MenuItem value="cream">Cream</MenuItem>
                    <MenuItem value="other">Other</MenuItem>
                  </FormikSelect>
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormikTextField
                    name="strength"
                    label="Strength"
                    placeholder="e.g., 500mg"
                    fullWidth
                  />
                </Grid>
              </Grid>
            </Box>


            <Box sx={{ marginBottom: 4 }}>
              <Typography
                variant="title1"
                component="h2"
                sx={{
                  fontWeight: 500,
                  marginBottom: 3,
                  color: "#333",
                  paddingBottom: 1,
                  borderBottom: "1px solid #e0e0e0",
                }}
              >
                Manufacturer Information
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <FormikTextField
                    name="manufacturer_name"
                    label="Manufacturer Name"
                    placeholder="Enter manufacturer name"
                    fullWidth
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <Box sx={{
                    '& input': {
                      border: 'none !important',
                      outline: 'none !important',
                      boxShadow: 'none !important',
                    },
                    '& input:focus': {
                      border: 'none !important',
                      outline: 'none !important',
                      boxShadow: 'none !important',
                    },
                  }}>
                    <Autocomplete
                      label="Country of Origin"
                      placeholder="Search for a country..."
                      source={COUNTRIES}
                      value={values.country_of_origin || null}
                      onChange={(event, newValue) => {
                        setFieldValue('country_of_origin', newValue);
                      }}
                      onBlur={() => {
                        setTouched({ ...touched, country_of_origin: true });
                      }}
                      error={!!(touched.country_of_origin && formikErrors.country_of_origin)}
                      helperText={(touched.country_of_origin && formikErrors.country_of_origin) || "Select the country where the drug is manufactured"}
                      fullWidth
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#1976d2',
                            borderWidth: '1px',
                          },
                        },
                        '& .MuiOutlinedInput-input': {
                          '&:focus': {
                            outline: 'none !important',
                          },
                        },
                        '& input': {
                          border: 'none !important',
                          outline: 'none !important',
                          '&:focus': {
                            border: 'none !important',
                            outline: 'none !important',
                          },
                        },
                      }}
                    />
                  </Box>
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormikTextField
                    name="marketing_authorization_holder"
                    label="Marketing Authorization Holder"
                    placeholder="e.g., Local Pharma Ltd"
                    fullWidth
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormikTextField
                    name="local_agent_distributor"
                    label="Local Agent/Distributor"
                    placeholder="Enter local agent or distributor"
                    fullWidth
                  />
                </Grid>
              </Grid>
            </Box>


            <Box sx={{ marginBottom: 4 }}>
              <Typography
                variant="title1"
                component="h2"
                sx={{
                  fontWeight: 500,
                  marginBottom: 3,
                  color: "#333",
                  paddingBottom: 1,
                  borderBottom: "1px solid #e0e0e0",
                }}
              >
                Pricing Information
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <FormikTextField
                    name="proposed_price_local"
                    label="Proposed Price (Local)"
                    type="number"
                    inputProps={{ step: "0.01" }}
                    placeholder="Enter proposed price"
                    fullWidth
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormikSelect name="currency" label="Currency" fullWidth>
                    <MenuItem value="">Select currency</MenuItem>
                    <MenuItem value="EGP">Egyptian Pound (EGP)</MenuItem>
                    <MenuItem value="USD">USD</MenuItem>
                    <MenuItem value="EUR">EUR</MenuItem>
                    <MenuItem value="GBP">GBP</MenuItem>
                    <MenuItem value="SAR">SAR</MenuItem>
                  </FormikSelect>
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormikTextField
                    name="cif_price"
                    label="CIF Price"
                    type="number"
                    inputProps={{ step: "0.01" }}
                    placeholder="0.00"
                    fullWidth
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormikTextField
                    name="ex_factory_price"
                    label="Ex-Factory Price"
                    type="number"
                    inputProps={{ step: "0.01" }}
                    placeholder="0.00"
                    fullWidth
                  />
                </Grid>
              </Grid>
            </Box>


            <Box sx={{ marginBottom: 4 }}>
              <Typography
                variant="title1"
                component="h2"
                sx={{
                  fontWeight: 500,
                  marginBottom: 3,
                  color: "#333",
                  paddingBottom: 1,
                  borderBottom: "1px solid #e0e0e0",
                }}
              >
                Reference Country Prices
              </Typography>

              <FieldArray name="reference_country_prices">
                {({ push, remove }) => (
                  <>
                    {values.reference_country_prices.map((_, index) => {
                      const countryError = formikErrors.reference_country_prices?.[index]?.country;
                      const priceError = formikErrors.reference_country_prices?.[index]?.price;
                      const currencyError = formikErrors.reference_country_prices?.[index]?.currency;
                      
                      return (
                      <Grid container spacing={2} key={index} sx={{ mb: 2 }}>
                        <Grid
                          item
                          xs={12}
                          md={values.reference_country_prices.length > 1 ? 3.5 : 4}
                        >
                          <FormikTextField
                            name={`reference_country_prices.${index}.country`}
                            label="Country"
                            placeholder="Enter country name"
                            fullWidth
                              error={!!countryError}
                              helperText={countryError || "Enter reference country name"}
                          />
                        </Grid>
                        <Grid
                          item
                          xs={12}
                          md={values.reference_country_prices.length > 1 ? 3.5 : 4}
                        >
                          <FormikTextField
                            name={`reference_country_prices.${index}.price`}
                            label="Price"
                            type="number"
                            inputProps={{ step: "0.01" }}
                            placeholder="0.00"
                            fullWidth
                              error={!!priceError}
                              helperText={priceError || "Enter reference price"}
                          />
                        </Grid>
                        <Grid
                          item
                          xs={12}
                          md={values.reference_country_prices.length > 1 ? 3.5 : 4}
                        >
                          <FormikSelect
                            name={`reference_country_prices.${index}.currency`}
                            label="Currency"
                            fullWidth
                              error={!!currencyError}
                              helperText={currencyError || "Select currency"}
                          >
                            <MenuItem value="">Select currency</MenuItem>
                            <MenuItem value="USD">USD</MenuItem>
                            <MenuItem value="EUR">EUR</MenuItem>
                            <MenuItem value="GBP">GBP</MenuItem>
                            <MenuItem value="EGP">EGP</MenuItem>
                          </FormikSelect>
                        </Grid>
                        {values.reference_country_prices.length > 1 && (
                          <Grid
                            item
                            xs={12}
                            md={1.5}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              marginTop: "25px",
                            }}
                          >
                            <IconButton
                              onClick={() => remove(index)}
                              sx={{
                                backgroundColor: "#fff",
                                border: "1px solid #ddd",
                                width: 40,
                                height: 40,
                                "&:hover": {
                                  backgroundColor: "#f5f5f5",
                                },
                              }}
                              size="medium"
                            >
                              <Trash size={16} color="#666" />
                            </IconButton>
                          </Grid>
                        )}
                      </Grid>
                      );
                    })}

                    <Button
                      variant="outlined"
                      onClick={() =>
                        push({ country: "", price: "", currency: "" })
                      }
                      startIcon={<Plus />}
                      sx={{ mt: 1 }}
                    >
                      Add Reference Country
                    </Button>
                  </>
                )}
              </FieldArray>
            </Box>


            <Box sx={{ marginBottom: 4 }}>
              <Typography
                variant="title1"
                component="h2"
                sx={{
                  fontWeight: 500,
                  marginBottom: 3,
                  color: "#333",
                  paddingBottom: 1,
                  borderBottom: "1px solid #e0e0e0",
                }}
              >
                Documents & Certificates
              </Typography>

              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <FormikDocumentUpload
                    name="price_certificate_file_id"
                    label="Price Certificate"
                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.zip,.rar,.7z"
                    helperText="Upload one document. Supported formats: PDF, Word, Images, Compressed files (ZIP, RAR, 7z, etc.)"
                    multiple={false}
                    maxFiles={1}
                    validateFiles={true}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <FormikDocumentUpload
                    name="gmp_certificate_file_id"
                    label="GMP Certificate"
                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.zip,.rar,.7z"
                    helperText="Upload one document. Supported formats: PDF, Word, Images, Compressed files (ZIP, RAR, 7z, etc.)"
                    multiple={false}
                    maxFiles={1}
                    validateFiles={true}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <FormikDocumentUpload
                    name="market_authorization_certificate_file_id"
                    label="Market Authorization Certificate"
                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.zip,.rar,.7z"
                    helperText="Upload one document. Supported formats: PDF, Word, Images, Compressed files (ZIP, RAR, 7z, etc.)"
                    multiple={false}
                    maxFiles={1}
                    validateFiles={true}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <FormikDocumentUpload
                    name="packaging_samples_file_id"
                    label="Packaging & Labeling Samples"
                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.zip,.rar,.7z"
                    helperText="Upload one document. Supported formats: PDF, Word, Images, Compressed files (ZIP, RAR, 7z, etc.)"
                    multiple={false}
                    maxFiles={1}
                    validateFiles={true}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <FormikDocumentUpload
                    name="pricing_history_file_id"
                    label="Pricing History Documents"
                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.zip,.rar,.7z"
                    helperText="Upload one document. Supported formats: PDF, Word, Images, Compressed files (ZIP, RAR, 7z, etc.)"
                    multiple={false}
                    maxFiles={1}
                    validateFiles={true}
                  />
                </Grid>
              </Grid>
            </Box>


            <Box sx={{ marginBottom: 4 }}>
              <Typography
                variant="title1"
                component="h2"
                sx={{
                  fontWeight: 500,
                  marginBottom: 3,
                  color: "#333",
                  paddingBottom: 1,
                  borderBottom: "1px solid #e0e0e0",
                }}
              >
                Additional Information
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <FormikTextField
                    name="packaging_details"
                    label="Packaging Details"
                    placeholder="Describe packaging details..."
                    multiline
                    rows={3}
                    fullWidth
                  />
                </Grid>
              </Grid>
            </Box>


            <Box sx={{ marginBottom: 4 }}>
              <Typography
                variant="title1"
                component="h2"
                sx={{
                  fontWeight: 500,
                  marginBottom: 3,
                  color: "#333",
                  paddingBottom: 1,
                  borderBottom: "1px solid #e0e0e0",
                }}
              >
                Contact Information
              </Typography>

              <Grid container spacing={2} sx={{ alignItems: 'flex-start' }}>
                <Grid item xs={12} md={6}>
                  <Box sx={{
                    '& .MuiFormLabel-root': {
                      fontSize: '14px',
                      lineHeight: '23.8px',
                      color: 'rgb(51, 51, 51)',
                      height: '25.7969px',
                    },
                    '& .MuiFormLabel-root.Mui-focused': {
                      color: '#1976d2',
                    },
                    '& .MuiFormLabel-root.Mui-error': {
                      color: '#d32f2f',
                    },
                    '& .MuiFormControl-root': {
                      marginTop: 0,
                    },
                  }}>
                    <FormikTextField
                      name="contact_email"
                      label="Contact Email"
                      type="email"
                      placeholder="contact@pharma.com"
                      fullWidth
                    />
                  </Box>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Box sx={{ 
                    '& > div, & [class*="PhoneNumberInput-wrapper"]': {
                      marginTop: '0 !important',
                    },
                    '& .react-tel-input': {
                      marginTop: '0 !important',
                    },
                    '& .react-tel-input .form-control': {
                      width: '100%',
                      height: '56px',
                      fontSize: '16px',
                      paddingTop: '16.5px',
                      paddingBottom: '16.5px',
                    },
                    '& .react-tel-input .flag-dropdown': {
                      backgroundColor: 'transparent',
                      borderRight: 'none',
                    },
                    '& > div > label': {
                      fontSize: '14px',
                      lineHeight: '23.8px',
                      color: 'rgb(51, 51, 51)',
                      height: '25.7969px',
                      transform: 'none',
                    },
                  }}>
                    <PhoneNumberInput
                      label="Phone Number"
                      helperText={formikErrors.phone_number || "Enter phone number with country code"}
                      value={values.phone_number || ''}
                      onChange={(value) => {
                        setFieldValue('phone_number', value);
                      }}
                      error={!!(formikErrors.phone_number)}
                      defaultCountry="eg"
                      fullWidth
                    />
                  </Box>
                </Grid>
              </Grid>
            </Box>


            <Box sx={{ marginBottom: 4 }}>
              <Typography
                variant="title1"
                component="h2"
                sx={{
                  fontWeight: 500,
                  marginBottom: 3,
                  color: "#333",
                  paddingBottom: 1,
                  borderBottom: "1px solid #e0e0e0",
                }}
              >
                Remarks & Justification
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <FormikTextField
                    name="remarks_justification"
                    label="Remarks & Justification"
                    placeholder="Provide explanation for pricing or any additional remarks..."
                    multiline
                    rows={4}
                    fullWidth
                  />
                </Grid>
              </Grid>
            </Box>


            <Box
              sx={{ 
                display: "flex", 
                alignItems: "center",
                marginTop: 3,
                padding: 2,
                backgroundColor: validity.drugPricing ? '#e8f5e8' : '#f5f5f5',
                borderRadius: 1,
                border: validity.drugPricing ? '1px solid #4caf50' : '1px solid #ddd',
                marginBottom: 5
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {validity.drugPricing && (
                  <StatusCheck size={20} color="#4caf50" />
                )}
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: validity.drugPricing ? '#4caf50' : '#666',
                    fontWeight: validity.drugPricing ? 'bold' : 'normal'
                  }}
                >
                  {validity.drugPricing 
                    ? 'Pricing form is valid and ready for submission' 
                    : 'Complete all required fields to enable submission'
                  }
                </Typography>
              </div>
            </Box>
            

            {displayErrors && Object.keys(displayErrors).length > 0 && (() => {
              const filteredErrors = Object.entries(displayErrors).filter(([field]) => 
                field !== 'reference_country_prices'
              );
              
              return filteredErrors.length > 0 && (
                <Box sx={{ marginTop: 2, padding: 2, backgroundColor: '#ffebee', borderRadius: 1 }}>
                  <Typography variant="body2" color="error" fontWeight="bold">
                    Please fix the following errors:
                  </Typography>
                  <ul style={{ margin: '8px 0', paddingLeft: 20 }}>
                    {filteredErrors.map(([field, error]) => {
                      const renderError = (err, fieldName) => {
                        if (fieldName === 'general' && typeof err === 'string') {
                          return err;
                        }
                        if (typeof err === 'string') {
                          return err;
                        }
                        if (Array.isArray(err)) {
                          return err.map((item, index) => {
                            if (typeof item === 'string') {
                              return `${fieldName}[${index}]: ${item}`;
                            }
                            if (typeof item === 'object' && item !== null) {
                              return Object.entries(item).map(([subField, subError]) => 
                                `${fieldName}[${index}].${subField}: ${subError}`
                              ).join(', ');
                            }
                            return `${fieldName}[${index}]: Invalid value`;
                          }).join('; ');
                        }
                        if (typeof err === 'object' && err !== null) {
                          return Object.entries(err).map(([subField, subError]) => 
                            `${fieldName}.${subField}: ${subError}`
                          ).join(', ');
                        }
                        return `${fieldName}: ${String(err)}`;
                      };

                      return (
                        <li key={field}>
                          <Typography variant="caption" color="error">
                            {renderError(error, field)}
                          </Typography>
                        </li>
                      );
                    })}
                  </ul>
                </Box>
              );
            })()}
          </Form>
          </>
          );
        }}
      </Formik>
      {/* Comments Section */}
      <div 
        className="form-comment-section" 
        style={{ 
          isolation: 'isolate', 
          position: 'relative', 
          zIndex: 1,
          marginTop: '35px'
        }}
        >
        <CommentsList
          prevComments={comments}
          pagination={{}}
          filters={{}}
          updateFilters={() => { }}
          showControls={false}
          showFilters={false}
          showSorting={false}
          showPageSize={false}
          showResultsInfo={false}
          showPagination={false}
            persona="customer"
            onEdit={handleEditComment}
            onDelete={handleDeleteComment}
          />
          {
            isSendbackMode &&
            <CommentToggle
              onAddComment={handleAddComment}
              persona={"customer"}
              buttonText="Comment"
              buttonProps={{ sx: { marginTop: 2, marginBottom: 2 } }}
              hasComments={comments && comments.length > 0}
            />
          }
      </div>
    </Paper>
    </>
  );
};

export default DrugPricingForm;
